/*
  Phone search overlay.

  While the Fast Simon search field has focus on a phone, this pins the header
  to the top of the screen and slides it up by the height of the row above the
  field, so the field lands at the top edge and the logo row goes off-screen.
  That hands roughly 86px back to the autocomplete panel, which on a phone with
  the keyboard open is the difference between one and a half results and four.
  See assets/search-overlay.css for the measurements.

  What this file owns, and deliberately does not:

    - it owns the light DOM only: a class on <body>, three custom properties on
      <html>, and one correction to an already-open panel's inline `top`;
    - it does NOT own the panel's height, skin, or scrolling. Those live in
      search-bar.js and search-panel.css.

  The panel's own max-height needs no signal from here: search-bar.js measures
  the room below the panel's live rect.top every frame, so moving the header
  simply makes its measurement bigger.
*/

(function () {
  'use strict';

  var PHONE_QUERY = '(max-width: 767px)';
  /* The theme's own header search input, not Fast Simon's runtime class.

     This mattered: `input.fast-autocomplete-input` works on a dev server but
     matches nothing on the storefront at the moment it is needed. Fast Simon
     binds lazily and only on a trusted click - it adds its classes and mounts
     its shadow host when the shopper first taps the field - so on a real page
     load there is no .fast-autocomplete-input in the document at all, and the
     focusin that should raise the overlay is the very event that arrives before
     the class does. Verified on the storefront: nine seconds after load the only
     search input present was `input#Search-In-Modal.search__input`, with no
     .fast-ac-container anywhere.

     The theme renders .header__search server-side, so this matches from first
     paint. Scoping to the header keeps the overlay off the search results
     page's own field, which is not in a header and has nothing to pin. */
  var FIELD_SELECTOR =
    '.header__search input[type="search"], .header__search input[name="q"]';
  var HOST_SELECTOR = '.fast-ac-container, .mobile-fast-ac-container';
  var PANEL_SELECTOR = '.fast-simon-autocomplete';
  var BODY_CLASS = 'search-overlay';

  /* The gap between the bottom of the pinned field and the top of the panel,
     while the overlay is up. It is a constant rather than the widget's own
     spacing because the widget's spacing is not recoverable here: Fast Simon
     anchors the panel once, in page coordinates, and never re-reads the field
     (verified - reopening the panel with the overlay up put it back at the
     pre-overlay position, 102px below the field). So there is nothing to
     preserve, and 8px matches the gap this file leaves above the field. */
  var PANEL_GAP = 8;


  /* The gap below the field once the bar has dropped back down.

     This used to be 18px - the widget's own rendered spacing, measured on the
     control panel at both 375x812 and 414x896 - and it used to be learned from
     the page at runtime rather than fixed. Both of those are gone on purpose.

     The widget's 18px existed to house the pointer arrow, and the arrow has been
     retired. What was left was 18px of nothing between the bar and the results,
     which read as the results sitting too low the moment the keyboard closed and
     the bar dropped. Matching PANEL_GAP means the panel is tucked under the bar
     the same way whether the keyboard is up or down - closing the keyboard now
     only makes the list taller, it does not move it away from the bar.

     Learning the number from the page went with it: what it learned was the
     widget's arrow spacing, which is exactly the value no longer wanted. */
  var restGap = PANEL_GAP;

  var root = document.documentElement;
  var active = false;
  var ticking = false;
  var blurTimer = null;

  function isPhone() {
    return window.matchMedia(PHONE_QUERY).matches;
  }

  function header() {
    return document.querySelector('header.custom-header');
  }

  /* The field the shopper is actually in. The theme renders more than one
     Fast Simon input across its breakpoints, and only the focused one is the
     right thing to measure. */
  function focusedField() {
    var el = document.activeElement;
    return el && el.matches && el.matches(FIELD_SELECTOR) ? el : null;
  }

  function anyField() {
    return focusedField() || document.querySelector(FIELD_SELECTOR);
  }

  function openPanels() {
    var found = [];
    document.querySelectorAll(HOST_SELECTOR).forEach(function (host) {
      if (!host.shadowRoot) return;
      host.shadowRoot.querySelectorAll(PANEL_SELECTOR).forEach(function (panel) {
        if (panel.getBoundingClientRect().height > 0) found.push(panel);
      });
    });
    return found;
  }

  /* ---------------------------------------------------------------------
     The offset.

     How far the search row sits below the header's own top edge. Measured
     rather than hardcoded because it is the height of the logo row, and that
     depends on the logo size the merchant picked in the theme editor - a taller
     logo means more to reclaim, not a broken layout.

     Read before the header is pinned, because pinning is what invalidates it.
     --------------------------------------------------------------------- */

  function rowOffset() {
    var hdr = header();
    var field = anyField();
    if (!hdr || !field) return 0;
    /* The field's own row, not the field: the row carries the padding that has
       to travel with it, and on this header the field is nested a few levels
       inside .header__icons. */
    var row = field.closest('.header__icons') || field;
    var offset = row.getBoundingClientRect().top - hdr.getBoundingClientRect().top;
    /* A negative or absurd value means the header is mid-transition or laid out
       in a way this does not model; doing nothing is always safe here. */
    return offset > 0 && offset < 400 ? Math.round(offset) : 0;
  }

  /* Re-measured continuously rather than captured once at activation.

     The offset is internal to the header - the distance from its top edge down
     to the search row - so it stays meaningful after the header is pinned, and
     re-reading it costs one rect. Capturing it once was wrong in a way that only
     showed on a real page: at the moment the shopper taps the field the logo
     image and the webfont may not have finished loading, and when they do, the
     search row moves. Measured on this store the offset settled from 28px to
     61px a few hundred milliseconds after focus, which left the logo row half on
     screen and gave back half the height the overlay had just reclaimed. */
  function writeRowOffset() {
    var offset = rowOffset();
    if (!offset) return;
    var value = offset + 'px';
    if (root.style.getPropertyValue('--so-row-offset') !== value) {
      root.style.setProperty('--so-row-offset', value);
    }
  }

  /* iOS scrolls the *visual* viewport under an open keyboard while leaving the
     layout viewport - the box position: fixed is resolved against - where it
     was. Adding offsetTop back is what keeps the pinned bar on screen there. */
  function shift() {
    var vv = window.visualViewport;
    return vv ? Math.round(vv.offsetTop) : 0;
  }

  function writeShift() {
    var value = shift() + 'px';
    if (root.style.getPropertyValue('--so-shift') !== value) {
      root.style.setProperty('--so-shift', value);
    }
  }

  /* Only while the overlay is up, and it stops the moment it comes down. The
     visual viewport moves during a keyboard animation without firing an event
     per frame, and a bar that lags the keyboard by a few frames is the kind of
     jitter people notice. */
  /* The panel has to keep following the field after the overlay comes down, not
     only while it is up.

     This is the bug this file was revised for. Dismissing the iPhone keyboard
     blurs the field, the overlay retracts, and the bar drops back to its place
     under the header - but the panel stayed where it was, sitting over the bar
     it belongs to. The original code handled that by restoring the inline `top`
     the widget had written before the overlay moved anything. That is only
     correct if nothing re-anchored the panel in between, and Fast Simon does
     re-anchor: on a real device the value it had written by then described the
     pinned position, so restoring it put the panel back exactly where it
     already wrongly was.

     Restoring a remembered number cannot be made reliable, because the thing it
     remembers is not owned by this file. Measuring where the field actually is
     can be, so the panel is tracked for as long as it is open on a phone and the
     remembered number is used for nothing. */
  function shouldTrack() {
    return isPhone() && openPanels().length > 0;
  }

  /* Fast Simon rebuilds the panel on every keystroke, and for a frame or two
     during a rebuild there is no panel with a height - which is indistinguishable
     from the shopper having closed it. Stopping on the first empty frame would
     drop tracking mid-typing and hand the panel back to the widget halfway
     through a search. Waiting out a short grace costs a rect read per frame and
     removes the whole class of flicker. */
  var GRACE_FRAMES = 60;
  var idleFrames = 0;

  function tick() {
    if (active || shouldTrack()) {
      idleFrames = 0;
    } else if (++idleFrames > GRACE_FRAMES) {
      ticking = false;
      idleFrames = 0;
      /* Hand the panel back to the widget as it was found, so nothing of this
         file's is left on an element it no longer manages. */
      releasePanels();
      return;
    }
    if (active) {
      writeShift();
      writeRowOffset();
    }
    syncPanels(active ? PANEL_GAP : restGap);
    requestAnimationFrame(tick);
  }

  function startTicking() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(tick);
  }

  /* ---------------------------------------------------------------------
     Keeping the panel with its field.

     Fast Simon writes the panel's `top` inline, once, from where the field was
     when the panel opened - and it does not rewrite it when the panel is
     rebuilt on the next keystroke. Pinning the header therefore leaves the
     panel floating where the field used to be, which would hand back every
     pixel this file just reclaimed.

     Rather than model the widget's anchoring - search-panel.css corrects the
     widget's gap with a negative margin - this measures the gap that actually
     rendered and closes it. The panel's inline `top` is nudged by the
     difference between where the panel is and where it should be, so it
     converges in a single frame no matter how the widget positioned it or what
     margins the panel stylesheet applies.

     The original value is kept so deactivate() can hand the panel back to the
     widget exactly as it found it.
     --------------------------------------------------------------------- */

  function syncPanels(gap) {
    var field = anyField();
    if (!field) return;
    var fieldBottom = field.getBoundingClientRect().bottom;

    openPanels().forEach(function (panel) {
      var wanted = fieldBottom + gap;

      var current = parseFloat(panel.style.top);
      if (!isNaN(current)) {
        var delta = wanted - panel.getBoundingClientRect().top;
        /* Sub-pixel differences are the panel already being in the right place;
           writing them back every frame would be churn. An absurd difference
           means something else is moving the panel and this should keep its
           hands off rather than chase it. */
        if (Math.abs(delta) >= 1 && Math.abs(delta) <= 2000) {
          if (!panel.hasAttribute('data-so-top')) {
            panel.setAttribute('data-so-top', panel.style.top);
          }
          panel.style.top = current + delta + 'px';
        }
      }
    });
  }

  function releasePanels() {
    document.querySelectorAll(HOST_SELECTOR).forEach(function (host) {
      if (!host.shadowRoot) return;
      host.shadowRoot.querySelectorAll('[data-so-top]').forEach(function (panel) {
        panel.style.top = panel.getAttribute('data-so-top');
        panel.removeAttribute('data-so-top');
      });
    });
  }

  function activate() {
    if (active || !isPhone()) return;
    var hdr = header();
    var field = anyField();
    if (!hdr || !field) return;

    var offset = rowOffset();
    if (!offset) return;

    /* Read the page's own background rather than naming a colour here, so the
       pinned bar matches whatever template the shopper is on. */
    var pageBg = getComputedStyle(document.body).backgroundColor;
    if (pageBg && pageBg !== 'rgba(0, 0, 0, 0)') {
      root.style.setProperty('--so-bar-bg', pageBg);
    }
    root.style.setProperty('--so-row-offset', offset + 'px');
    writeShift();

    active = true;
    document.body.classList.add(BODY_CLASS);
    startTicking();

    /* A panel that is already open is left behind by the header move, so it is
       brought along in the same frame rather than on the next one. */
    observeHosts();
    syncPanels(PANEL_GAP);
  }

  function deactivate() {
    if (!active) return;
    active = false;
    document.body.classList.remove(BODY_CLASS);
    root.style.removeProperty('--so-row-offset');
    root.style.removeProperty('--so-shift');
    root.style.removeProperty('--so-bar-bg');

    /* Deliberately no releasePanels() here. The bar has just dropped back under
       the header, and an open panel has to travel down with it - that is the
       whole fix. The frame loop keeps running on a phone for exactly as long as
       the panel is open, and releases it when it closes. */
    if (shouldTrack()) {
      syncPanels(restGap);
      startTicking();
    } else {
      releasePanels();
    }
  }

  /* Focus leaves the input for reasons that are not the shopper leaving the
     search - tapping the panel can move focus inside the shadow root, and iOS
     briefly blurs during scroll momentum. Settling for a frame and then asking
     where focus actually landed avoids the header snapping back and forth
     under the shopper's thumb. */
  function scheduleDeactivate() {
    clearTimeout(blurTimer);
    blurTimer = setTimeout(function () {
      if (!focusedField()) deactivate();
    }, 120);
  }

  document.addEventListener('focusin', function (event) {
    if (event.target && event.target.matches && event.target.matches(FIELD_SELECTOR)) {
      clearTimeout(blurTimer);
      activate();
    }
  });

  /* Watch the shadow roots, rather than guessing when the panel will exist.

     The panel is rendered from a network response, so it does not exist on the
     frame the keystroke fires, and how long it takes is a property of the
     shopper's connection. Timers were tried and are the wrong tool: a 450ms
     guess left the panel unpositioned on a slow response, and the only other
     thing that would have caught it - the frame loop - is paused whenever the
     document is hidden and throttled in a background tab.

     Observing the shadow root instead means the panel is positioned on the frame
     it appears, on any connection, in any tab state. This is the same mechanism
     search-bar.js already uses to re-apply itself when the widget rebuilds. */
  function observeHosts() {
    document.querySelectorAll(HOST_SELECTOR).forEach(function (host) {
      if (!host.shadowRoot || host.dataset.soObserved) return;
      host.dataset.soObserved = 'true';
      new MutationObserver(function () {
        refresh();
      }).observe(host.shadowRoot, { childList: true, subtree: true });
    });
  }

  document.addEventListener('input', function (event) {
    if (!event.target || !event.target.matches || !event.target.matches(FIELD_SELECTOR)) return;
    /* Fast Simon mounts its hosts lazily, on the shopper's first real tap, so
       the roots to observe may not have existed until this moment. */
    observeHosts();
    refresh();
  });

  document.addEventListener('focusout', function (event) {
    if (event.target && event.target.matches && event.target.matches(FIELD_SELECTOR)) {
      scheduleDeactivate();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') deactivate();
  });

  /* requestAnimationFrame is paused while the document is hidden and throttled
     in background tabs, so a design that only ever moved the panel from the
     frame loop would leave it stranded in those states - and would then never
     recover, because startTicking() sees `ticking` already true. Every signal
     that can move the field or the visible viewport therefore does the work
     directly as well as making sure the loop is running. */
  function refresh() {
    if (!active && !shouldTrack()) return;
    if (active) {
      writeShift();
      writeRowOffset();
    }
    syncPanels(active ? PANEL_GAP : restGap);
    startTicking();
  }

  var vv = window.visualViewport;
  if (vv) {
    vv.addEventListener('resize', refresh);
    vv.addEventListener('scroll', refresh);
  }
  window.addEventListener('resize', refresh);
  window.addEventListener('scroll', refresh, { passive: true });
  document.addEventListener('visibilitychange', function () {
    /* Coming back from hidden is the case the loop cannot recover from on its
       own: it stopped without clearing its own flag. */
    ticking = false;
    refresh();
  });

  /* Rotating to landscape, or resizing past the phone breakpoint, has to put
     the header back rather than leave it pinned in a layout this was never
     measured against. */
  window.addEventListener('orientationchange', function () {
    if (!isPhone()) deactivate();
  });

  var phoneQuery = window.matchMedia(PHONE_QUERY);
  var onQueryChange = function () {
    if (!isPhone()) deactivate();
  };
  if (phoneQuery.addEventListener) {
    phoneQuery.addEventListener('change', onQueryChange);
  } else if (phoneQuery.addListener) {
    phoneQuery.addListener(onQueryChange);
  }
})();
